(function ($) {
    const SELECTOR = '.logo-list[data-logo-marquee]';
    const SPEED_PX_PER_SEC = 35;
    const RESIZE_DEBOUNCE_MS = 200;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const isIOS = (() => {
        const ua = navigator.userAgent || '';
        const iOSDevice = /iPad|iPhone|iPod/.test(ua);
        const iPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
        return iOSDevice || iPadOS;
    })();

    let lastViewportWidth = window.innerWidth;

    function debounce(fn, wait) {
        let t;
        return function () {
            clearTimeout(t);
            t = setTimeout(() => fn.apply(this, arguments), wait);
        };
    }

    function raf2() {
        return new Promise((resolve) => {
            requestAnimationFrame(() => requestAnimationFrame(resolve));
        });
    }

    function isOverflowing(el) {
        return el.scrollWidth > el.clientWidth + 1;
    }

    function getState($el) {
        return $el.data('logoMarqueeState');
    }

    function setState($el, state) {
        $el.data('logoMarqueeState', state);
    }

    function shouldRefreshOnResize() {
        const w = window.innerWidth;
        if (Math.abs(w - lastViewportWidth) > 1) {
            lastViewportWidth = w;
            return true;
        }
        return false;
    }

    function waitForImages($scope) {
        const imgs = $scope.find('img').toArray();
        if (!imgs.length) return Promise.resolve();

        imgs.forEach((img) => {
            try { img.loading = 'eager'; } catch (e) {}
        });

        const promises = imgs.map((img) => {
            const decodeIfPossible = () => {
                if (img.decode) return img.decode().catch(() => {});
                return Promise.resolve();
            };

            if (img.complete && img.naturalWidth) {
                return decodeIfPossible();
            }

            return new Promise((resolve) => {
                $(img).one('load error', () => {
                    decodeIfPossible().finally(resolve);
                });
            });
        });

        return Promise.all(promises).then(() => {});
    }

    function cleanup($container) {
        const state = getState($container);

        if (state) {
            if (state.rafId) cancelAnimationFrame(state.rafId);
            if (state.ro) state.ro.disconnect();

            if (state.$clone && state.$clone.length) state.$clone.remove();

            if (state.$track && state.$track.length) {
                state.$track.children('ul').each(function () {
                    $container.append(this);
                });
                state.$track.remove();
            }
        }

        $container.removeClass('is-marquee is-marquee--transform');
        $container.off('.logoMarquee');

        const el = $container.get(0);
        el.scrollLeft = 0;

        setState($container, null);
    }

    async function start($container) {
        const el = $container.get(0);

        if (prefersReduced) {
            cleanup($container);
            return;
        }

        const $ul = $container.children('ul').first();
        if (!$ul.length) return;

        if (getState($container)) cleanup($container);

        if (!isOverflowing(el)) return;

        await waitForImages($container);
        await raf2();

        if (!isOverflowing(el)) return;

        const state = {
            isPaused: false,
            lastTs: null,
            rafId: null,
            ro: null,
            $clone: null,
            $track: null,
            loopWidth: 0,
            x: 0
        };

        function pause() {
            state.isPaused = true;
        }

        function resume() {
            state.isPaused = false;
            state.lastTs = null;
        }

        $container
            .on('mouseenter.logoMarquee focusin.logoMarquee touchstart.logoMarquee', pause)
            .on('mouseleave.logoMarquee focusout.logoMarquee touchend.logoMarquee touchcancel.logoMarquee', resume);

        if (isIOS) {
            const $track = $('<div class="logo-marquee-track" />');
            $ul.detach().appendTo($track);

            const $clone = $ul.clone(true, true)
                .attr('aria-hidden', 'true')
                .addClass('is-clone');

            $clone.find('[id]').removeAttr('id');
            $clone.appendTo($track);

            $track.appendTo($container);

            $container.addClass('is-marquee is-marquee--transform');

            state.$track = $track;
            state.$clone = $clone;
            state.x = 0;

            function measureLoopWidth() {
                const firstUl = $track.children('ul').first().get(0);
                state.loopWidth = firstUl ? firstUl.getBoundingClientRect().width : 0;
            }

            if (window.ResizeObserver) {
                state.ro = new ResizeObserver(async () => {
                    await raf2();
                    measureLoopWidth();
                });
                state.ro.observe($track.children('ul').first().get(0));
            }

            measureLoopWidth();

            function tick(ts) {
                if (!state.lastTs) state.lastTs = ts;
                const dt = (ts - state.lastTs) / 1000;
                state.lastTs = ts;

                if (!state.isPaused && state.loopWidth > 0) {
                    state.x -= SPEED_PX_PER_SEC * dt;

                    if (Math.abs(state.x) >= state.loopWidth) {
                        state.x += state.loopWidth;
                    }

                    $track.css('transform', `translate3d(${state.x}px, 0, 0)`);
                }

                state.rafId = requestAnimationFrame(tick);
                setState($container, state);
            }

            state.rafId = requestAnimationFrame(tick);
            setState($container, state);
            return;
        }

        const $clone = $ul.clone(true, true)
            .attr('aria-hidden', 'true')
            .addClass('is-clone');

        $clone.find('[id]').removeAttr('id');
        $container.append($clone);

        $container.addClass('is-marquee');

        state.$clone = $clone;

        function measureLoopWidth() {
            const ulRect = $ul.get(0).getBoundingClientRect();
            const cloneRect = $clone.get(0).getBoundingClientRect();
            const w = cloneRect.left - ulRect.left;
            state.loopWidth = w > 0 ? w : 0;
        }

        if (window.ResizeObserver) {
            state.ro = new ResizeObserver(async () => {
                await raf2();
                measureLoopWidth();
            });
            state.ro.observe($ul.get(0));
        }

        measureLoopWidth();

        function tick(ts) {
            if (!state.lastTs) state.lastTs = ts;
            const dt = (ts - state.lastTs) / 1000;
            state.lastTs = ts;

            if (!state.isPaused && state.loopWidth > 0) {
                el.scrollLeft = el.scrollLeft + (SPEED_PX_PER_SEC * dt);

                if (el.scrollLeft >= state.loopWidth - 0.5) {
                    el.scrollLeft -= state.loopWidth;
                }
            }

            state.rafId = requestAnimationFrame(tick);
            setState($container, state);
        }

        state.rafId = requestAnimationFrame(tick);
        setState($container, state);
    }

    function refreshAll() {
        $(SELECTOR).each(function () {
            const $container = $(this);
            cleanup($container);
            start($container);
        });
    }

    $(function () {
        refreshAll();

        $(window).on('resize', debounce(function () {
            if (shouldRefreshOnResize()) {
                refreshAll();
            }
        }, RESIZE_DEBOUNCE_MS));

        window.addEventListener('load', refreshAll);

        window.addEventListener('orientationchange', debounce(function () {
            lastViewportWidth = window.innerWidth;
            refreshAll();
        }, RESIZE_DEBOUNCE_MS));
    });
})(jQuery);
